// CheckinDashboard — der Schalter am Turniertag.
//
// Bis zum 16.09.2026 gab es diese Seite nicht. Die Schnittstelle war
// vollständig da (Liste laden, Haken setzen, Spalten in der Datenbank), nur
// rief sie niemand auf: 0 von 41 Anmeldungen eingecheckt, und keine
// Möglichkeit, das zu ändern.
//
// ⚠️ Eingecheckt wird die PERSON, nicht die einzelne Meldung. Wer in vier
// Kategorien startet, kommt einmal an den Schalter. Der Server macht das schon
// so (ein PATCH setzt alle Meldungen der Person); hier wird entsprechend
// gruppiert, damit die Liste nicht viermal denselben Menschen zeigt.
//
// ⚠️ Bezahlt wird je VEREIN, nicht je Person — so läuft es am Schalter, und so
// entsteht die Rechnung (sie entsteht bewusst erst beim Bezahlen: eine vorher
// gestellte Rechnung müsste man bei jeder Abmeldung wieder stornieren).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CheckinDashboard.css';
import CheckinBereinigung from './CheckinBereinigung';
import { kategorieText } from '../../lib/kategorieText';
import { toast } from '../common/Toast';

const kopf = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const euro = (b) => {
  const n = parseFloat(b);
  return Number.isFinite(n) ? n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : '–';
};

const uhrzeit = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null
    : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

// nurCheckin: Ansicht für das Registrierungs-Personal (RegStaffDashboard) —
// ohne Kasse und ohne Bereinigung, die bleiben beim Admin.
export default function CheckinDashboard({ selectedTurnier, nurCheckin = false }) {
  const turnierId = selectedTurnier?.turnier_id || selectedTurnier?.id;

  const [zeilen, setZeilen]   = useState([]);
  const [geld, setGeld]       = useState([]);
  const [laden, setLaden]     = useState(false);
  const [fehler, setFehler]   = useState('');
  const [suche, setSuche]     = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [arbeitet, setArbeitet] = useState({});

  const [stand, setStand]     = useState(null); // Zeitpunkt der letzten geladenen Liste

  // still = Hintergrund-Abfrage alle 15 s: kein "lädt…", und ein Fehler
  // überschreibt keine Meldung des Benutzers, sondern steht am Stand.
  const laden_ = useCallback(async (still = false) => {
    if (!turnierId) return;
    if (!still) { setLaden(true); setFehler(''); }
    try {
      const [rCheck, rGeld] = await Promise.all([
        fetch(`/api/anmeldungen/turnier/${turnierId}/checkin`, { headers: kopf() }),
        nurCheckin ? Promise.resolve(null) : fetch(`/api/rechnungen/turnier/${turnierId}`, { headers: kopf() }),
      ]);
      const dCheck = await rCheck.json().catch(() => ({}));
      if (!rCheck.ok || !dCheck.success) throw new Error(dCheck.error || `Liste konnte nicht geladen werden (${rCheck.status})`);
      setZeilen(dCheck.data || []);
      setStand({ zeit: new Date(), ok: true });

      // ⚠️ Die Geldübersicht darf den Check-in nicht aufhalten. Sie ist für
      // Vereinsadmins gesperrt; ohne sie fehlt nur die Spalte, nicht die Seite.
      try {
        const dGeld = rGeld && rGeld.ok ? await rGeld.json() : null;
        setGeld(dGeld?.data?.vereine || []);
      } catch { setGeld([]); }
    } catch (e) {
      if (still) setStand((alt) => ({ zeit: alt?.zeit || null, ok: false }));
      else setFehler(e.message || 'Fehler beim Laden');
    } finally {
      if (!still) setLaden(false);
    }
  }, [turnierId, nurCheckin]);

  useEffect(() => { laden_(); }, [laden_]);

  // Mehrere Tablets am Empfang: jedes sieht nach spätestens 15 s, was die
  // anderen eingecheckt haben (18.10.2026; vorher nur einmal geladen).
  useEffect(() => {
    if (!turnierId) return undefined;
    const t = setInterval(() => { if (!document.hidden) laden_(true); }, 15000);
    return () => clearInterval(t);
  }, [turnierId, laden_]);

  // ── Eine Zeile je PERSON, nicht je Meldung ───────────────────────────────
  const personen = useMemo(() => {
    const karte = new Map();
    for (const z of zeilen) {
      const id = z.wettkampfer_id;
      if (!karte.has(id)) {
        karte.set(id, {
          wettkampfer_id: id,
          anmeldung_id: z.id,           // eine reicht — der Server setzt alle
          name: `${z.nachname}, ${z.vorname}`,
          verein: z.verein_name,
          verein_id: z.anmeldender_verein_id,
          gurtfarbe: z.gurtfarbe,
          eingecheckt: !!z.eingecheckt,
          checkin_zeit: z.checkin_zeit,
          divisionen: [],
          offen: 0,
        });
      }
      const p = karte.get(id);
      // Code mit Klartextname (18.10.2026); die Suche findet weiter den Code
      if (z.division_code) p.divisionen.push(kategorieText(z.division_code, z.division_name));
      // ⚠️ Teilweise eingecheckt gibt es nicht: der Server setzt immer alle
      // Meldungen einer Person. Sollte doch einmal etwas auseinanderlaufen,
      // gilt „noch nicht fertig" — lieber einmal zu viel nachfragen.
      if (!z.eingecheckt) p.eingecheckt = false;
      if (!z.bezahlt) p.offen += 1;
    }
    return [...karte.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [zeilen]);

  const geldJeVerein = useMemo(() => {
    const k = new Map();
    for (const v of geld) k.set(v.verein_id, v);
    return k;
  }, [geld]);

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return personen.filter((p) => {
      if (nurOffen && p.eingecheckt) return false;
      if (!s) return true;
      return p.name.toLowerCase().includes(s)
        || (p.verein || '').toLowerCase().includes(s)
        || p.divisionen.some((d) => d.toLowerCase().includes(s));
    });
  }, [personen, suche, nurOffen]);

  // ⚠️ Der Zähler je Verein zählt den ECHTEN Bestand, nicht die gefilterte
  // Ansicht. Sonst stünde bei einer Suche nach einem Namen „1 / 1 da" über einem
  // Verein, von dem elf Leute noch fehlen — eine Zahl, die genau dann lügt, wenn
  // man sie braucht.
  const bestandJeVerein = useMemo(() => {
    const k = new Map();
    for (const p of personen) {
      const key = p.verein || 'Ohne Verein';
      const e = k.get(key) || { gesamt: 0, da: 0 };
      e.gesamt += 1;
      if (p.eingecheckt) e.da += 1;
      k.set(key, e);
    }
    return k;
  }, [personen]);

  const nachVerein = useMemo(() => {
    const gruppen = new Map();
    for (const p of gefiltert) {
      const key = p.verein || 'Ohne Verein';
      if (!gruppen.has(key)) gruppen.set(key, { verein: key, verein_id: p.verein_id, leute: [] });
      gruppen.get(key).leute.push(p);
    }
    return [...gruppen.values()].sort((a, b) => a.verein.localeCompare(b.verein, 'de'));
  }, [gefiltert]);

  const eingecheckt = personen.filter((p) => p.eingecheckt).length;

  // Setzt den Stand, den der Knopf ZEIGT, ins Gegenteil — der Server bekommt
  // den gewollten Wert, nicht "umschalten". Hat ein anderes Tablet die Person
  // inzwischen eingecheckt, bleibt sie eingecheckt.
  const umschalten = async (person) => {
    setArbeitet((v) => ({ ...v, [person.wettkampfer_id]: true }));
    try {
      const r = await fetch(`/api/anmeldungen/${person.anmeldung_id}/checkin`, {
        method: 'PATCH', headers: kopf(), body: JSON.stringify({ eingecheckt: !person.eingecheckt }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || d.message || `Check-in fehlgeschlagen (${r.status})`);
      setZeilen((alt) => alt.map((z) => z.wettkampfer_id === person.wettkampfer_id
        ? { ...z, eingecheckt: d.eingecheckt ? 1 : 0, checkin_zeit: d.checkin_zeit }
        : z));
    } catch (e) {
      setFehler(e.message);
      toast.fehler(`${person.name}: ${e.message}`);
    } finally {
      setArbeitet((v) => ({ ...v, [person.wettkampfer_id]: false }));
    }
  };

  const bezahlen = async (vereinId, bezahlt) => {
    setArbeitet((v) => ({ ...v, [`v${vereinId}`]: true }));
    try {
      const r = await fetch(`/api/rechnungen/turnier/${turnierId}/verein/${vereinId}/bezahlt`, {
        method: 'PATCH', headers: kopf(), body: JSON.stringify({ bezahlt }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || 'Zahlung konnte nicht gespeichert werden');
      await laden_();
      toast.ok(bezahlt ? 'Als bezahlt gespeichert' : 'Zahlung zurückgenommen');
    } catch (e) {
      setFehler(e.message);
      toast.fehler(e.message);
    } finally {
      setArbeitet((v) => ({ ...v, [`v${vereinId}`]: false }));
    }
  };

  if (!turnierId) {
    return <div className="ci-hinweis">Bitte zuerst ein Turnier auswählen.</div>;
  }

  return (
    <div className={nurCheckin ? 'ci ci--dunkel' : 'ci'}>
      <div className="ci-kopf">
        <div>
          <h2>Check-in</h2>
          <p className="ci-sub">{selectedTurnier?.name}</p>
        </div>
        <div className="ci-zahlen">
          <div className="ci-zahl">
            <strong>{eingecheckt}</strong>
            <span>von {personen.length} da</span>
          </div>
          {geld.length > 0 && (
            <div className="ci-zahl">
              <strong>{euro(geld.reduce((s, v) => s + parseFloat(v.betrag_offen || 0), 0))}</strong>
              <span>offen</span>
            </div>
          )}
        </div>
      </div>

      {fehler && <div className="ci-fehler" role="alert">{fehler}</div>}

      <div className="ci-werkzeuge">
        <input
          className="ci-suche"
          type="search"
          placeholder="🔍 Name, Verein oder Kategorie…"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        <label className="ci-schalter">
          <input type="checkbox" checked={nurOffen} onChange={(e) => setNurOffen(e.target.checked)} />
          nur wer noch fehlt
        </label>
        <button className="ci-btn ci-btn--still" onClick={() => laden_()} disabled={laden}>
          {laden ? 'lädt…' : 'Neu laden'}
        </button>
        {stand?.zeit && (
          <span className={stand.ok ? 'ci-zeit' : 'ci-fehler'} style={stand.ok ? undefined : { padding: '0.2rem 0.5rem', margin: 0 }}>
            Stand {stand.zeit.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            {stand.ok ? '' : ' · Verbindung gestört'}
          </span>
        )}
      </div>

      {personen.length > 0 && !nurCheckin && <CheckinBereinigung turnierId={turnierId} onFertig={() => laden_()} />}

      {personen.length === 0 && !laden && (
        <div className="ci-hinweis">Für dieses Turnier ist niemand angemeldet.</div>
      )}

      {/* ⚠️ Eine leere Liste ohne Erklärung sieht aus wie ein Fehler. Hier steht,
          warum sie leer ist und wie man wieder herauskommt. */}
      {personen.length > 0 && gefiltert.length === 0 && (
        <div className="ci-hinweis">
          {nurOffen && suche.trim()
            ? <>Niemand fehlt mehr, auf den „{suche.trim()}" passt.</>
            : nurOffen
              ? <>Alle {personen.length} sind eingecheckt.</>
              : <>Kein Treffer für „{suche.trim()}".</>}
          {' '}
          <button className="ci-btn ci-btn--still" onClick={() => { setSuche(''); setNurOffen(false); }}>
            Filter zurücksetzen
          </button>
        </div>
      )}

      {nachVerein.map((g) => {
        const v = geldJeVerein.get(g.verein_id);
        const offenerBetrag = parseFloat(v?.betrag_offen || 0);
        const gesamtBetrag  = parseFloat(v?.betrag_gesamt || 0);
        const bestand = bestandJeVerein.get(g.verein) || { da: 0, gesamt: g.leute.length };
        return (
          <div className="ci-verein" key={g.verein}>
            <div className="ci-verein-kopf">
              <div>
                <strong>{g.verein}</strong>
                <span className="ci-verein-zahl">
                  {bestand.da} / {bestand.gesamt} da
                  {g.leute.length < bestand.gesamt && ` · ${g.leute.length} gezeigt`}
                </span>
              </div>
              {v && gesamtBetrag > 0 && (
                <div className="ci-geld">
                  <span className={offenerBetrag > 0 ? 'ci-offen' : 'ci-bezahlt'}>
                    {offenerBetrag > 0 ? `${euro(offenerBetrag)} offen` : `${euro(gesamtBetrag)} bezahlt`}
                  </span>
                  {/* Die Rechnung entsteht mit diesem Knopf — bewusst erst beim
                      Bezahlen. Eine vorher gestellte Rechnung müsste man bei
                      jeder Abmeldung wieder stornieren. */}
                  <button
                    className={`ci-btn ${offenerBetrag > 0 ? 'ci-btn--geld' : 'ci-btn--still'}`}
                    onClick={() => bezahlen(g.verein_id, offenerBetrag > 0)}
                    disabled={!!arbeitet[`v${g.verein_id}`]}
                  >
                    {offenerBetrag > 0 ? 'Vor Ort bezahlt' : 'Zahlung zurücknehmen'}
                  </button>
                </div>
              )}
              {/* ⚠️ Ohne Startgebühr gibt es nichts zu kassieren — dann hier auch
                  keinen Knopf, der „bezahlt" behauptet. Das trifft Turniere, bei
                  denen der Preis noch nicht gepflegt ist. */}
              {v && gesamtBetrag === 0 && <span className="ci-keine-gebuehr">keine Startgebühr hinterlegt</span>}
            </div>

            <ul className="ci-liste">
              {g.leute.map((p) => (
                <li key={p.wettkampfer_id} className={p.eingecheckt ? 'ci-da' : ''}>
                  <button
                    className={`ci-haken ${p.eingecheckt ? 'ci-haken--an' : ''}`}
                    onClick={() => umschalten(p)}
                    disabled={!!arbeitet[p.wettkampfer_id]}
                    aria-label={p.eingecheckt ? `${p.name} wieder abmelden` : `${p.name} einchecken`}
                  >
                    {p.eingecheckt ? '✓' : ''}
                  </button>
                  <div className="ci-person">
                    <span className="ci-name">{p.name}</span>
                    <span className="ci-starts">
                      {p.divisionen.length > 0
                        ? `${p.divisionen.length} ${p.divisionen.length === 1 ? 'Start' : 'Starts'}: ${p.divisionen.join('; ')}`
                        : 'noch ohne Kategorie'}
                    </span>
                  </div>
                  {p.eingecheckt && uhrzeit(p.checkin_zeit) && (
                    <span className="ci-zeit">{uhrzeit(p.checkin_zeit)} Uhr</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
